import { fabric } from 'fabric';
import type Table from '../objects/Table';
import ControlBase from '../fabric/ControlBase';
import { resizeWithSmartGuides } from '../objects/SmartGuideBehaviors';

const SIZE = 10;

const scaleMap = {
  se: 'nwse',
  sw: 'nesw',
  nw: 'nwse',
  ne: 'nesw',
};

const resizeCursorHandler = (eventData, control, fabricObject) => {
  const cursor = fabric.controlsUtils.scaleCursorStyleHandler(
    eventData,
    control,
    fabricObject
  );
  const cursorRegex = /^(.{1,2})-resize$/;
  const match = cursor.match(cursorRegex);

  if (!match) {
    throw new Error(`Invalid cursor string "${cursor}"`);
  }

  return `${scaleMap[match[1]]}-resize`;
};

const shouldResize = (corner, x, y, object, isRow = true) => {
  const { left, top } = object;
  const fromTopLeft =
    corner === 'mt' ||
    corner === 'ml' ||
    corner === 'tl' ||
    (corner === 'tr' && isRow) ||
    (corner === 'bl' && !isRow);
  if (fromTopLeft) {
    return isRow ? y < top : x < left;
  }
  return isRow ? y > top : x > left;
};

// Table scale never changes. Instead, we change the width/height and distribute
// that change across the table vectors
class TableResizeControl extends ControlBase {
  // Name of the action the control is used for.
  actionName: string;

  constructor(options) {
    super(options);
    this.sizeX = this.sizeY = this.touchSizeX = this.touchSizeY = SIZE;
    this.cursorStyleHandler = resizeCursorHandler;
    this.actionHandler = resizeWithSmartGuides(
      fabric.controlsUtils.wrapWithFixedAnchor(this.handler)
    );
  }

  getMouseDownHandler() {
    return (eventData, transform) => {
      const object = transform.target;
      object.clearSelectedCells();
      object.toggleResizing(true);
      return true;
    };
  }

  getMouseUpHandler(): fabric.ControlMouseEventHandler {
    return (e: MouseEvent, transform: fabric.Transform) => {
      const object = transform.target as Table;
      object.toggleResizing(false);
      object.canvas.controller.undoManager.completeUndoGroup();

      return false;
    };
  }

  handler(eventData, transform, x, y) {
    const object = transform.target;
    const localPoint = fabric.controlsUtils.getLocalPoint(
      transform,
      transform.originX,
      transform.originY,
      x,
      y
    );
    const strokePadding = object.strokeWidth;
    const newWidth = Math.abs(localPoint.x) - strokePadding;
    const newHeight = Math.abs(localPoint.y) - strokePadding;
    // Prevent resizing when cursor is past origin + dimension of object
    if (
      newWidth > object.width &&
      !shouldResize(transform.corner, x, y, object, false)
    ) {
      return false;
    }
    if (
      newHeight > object.height &&
      !shouldResize(transform.corner, x, y, object, true)
    ) {
      return false;
    }

    return object.handle2DResize(newHeight, newWidth, transform.corner);
  }

  render(ctx: CanvasRenderingContext2D, left: number, top: number) {
    const radius = 1;
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#FFF';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
    ctx.shadowBlur = 8;
    ctx.strokeStyle = '#B5B5B5';
    ctx.lineWidth = 1;
    left = left - SIZE / 2;
    top = top - SIZE / 2;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(left + radius, top);
    // Draw the top line of the rectangle
    ctx.lineTo(left + SIZE - radius, top);
    // Draw the top-right corner of the rectangle
    ctx.arcTo(left + SIZE, top, left + SIZE, top + radius, radius);
    // Draw the right side of the rectangle
    ctx.lineTo(left + SIZE, top + SIZE - radius);
    // Draw the bottom-right corner of the rectangle
    ctx.arcTo(
      left + SIZE,
      top + SIZE,
      left + SIZE - radius,
      top + SIZE,
      radius
    );
    // Draw the bottom side of the rectangle
    ctx.lineTo(left + radius, top + SIZE);
    // Draw the bottom-left corner of the rectangle
    ctx.arcTo(left, top + SIZE, left, top + SIZE - radius, radius);
    // Draw the left side of the rectangle
    ctx.lineTo(left, top + radius);
    // Draw the top-left corner of the rectangle
    ctx.arcTo(left, top, left + radius, top, radius);
    ctx.stroke();
    ctx.fill();
    ctx.restore();
  }
}
export default TableResizeControl;
